import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { inboxQuery, type InboxState } from '@/api/queries'
import { FilterBar } from '@/components/FilterBar'
import { EmptyState, PageHeader } from '@/components/page'
import { Skeleton } from '@/components/ui/skeleton'
import { InboxItemRow } from '@/features/inbox/inbox'

const STATES = ['open', 'acknowledged', 'all'] as const

export const Route = createFileRoute('/_app/inbox')({
  validateSearch: z.object({ state: z.enum(STATES).optional().catch(undefined) }),
  component: InboxPage,
})

function InboxPage() {
  const { t } = useTranslation()
  const { state = 'open' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data, isPending, isError, refetch } = useQuery(inboxQuery(state))

  return (
    <>
      <PageHeader title={t('inbox.title')} description={t('inbox.description')} />
      <FilterBar
        filters={[
          {
            id: 'state',
            kind: 'select',
            label: t('inbox.stateLabel'),
            value: state,
            options: STATES.map((value) => ({ value, label: t(`inbox.state.${value}`) })),
            onChange: (value) => void navigate({ search: { state: value as InboxState } }),
          },
        ]}
      />
      {isPending ? (
        <Skeleton className="h-24 w-full" aria-label={t('common.loading')} />
      ) : isError ? (
        <EmptyState title={t('errors.generic')} body={t('errors.network')}>
          <button className="underline" onClick={() => void refetch()}>
            {t('common.retry')}
          </button>
        </EmptyState>
      ) : data.unavailable ? (
        <EmptyState title={t('inbox.unavailableTitle')} body={t('inbox.unavailableBody')} />
      ) : data.data.length === 0 ? (
        <EmptyState title={t('inbox.emptyTitle')} body={t('inbox.emptyBody')} />
      ) : (
        <ul aria-label={t('inbox.listLabel')} className="grid gap-3">
          {data.data.map((item) => (
            <InboxItemRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </>
  )
}
